import { FiAlertTriangle, FiChevronDown, FiChevronUp, FiCopy, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import { FaBug, FaCopy } from "react-icons/fa";
import toast from "react-hot-toast";
import { useState } from "react";

import { Button } from "../ui";

type SentryErrorComponentProps = {
  error: unknown;
  componentStack?: string;
  eventId?: string;
  resetError: () => void;
};

export const ErrorBoundary = ({
  children,
  charts,
  onRemove,
}: {
  children: React.ReactNode;
  charts?: boolean;
  onRemove?: () => void;
}) => {
  return (
    <SentryErrorBoundary
      fallback={(x) => (charts ? <ErrorPreviewCharts {...x} onRemove={onRemove} /> : <ErrorPreview {...x} />)}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export const ErrorPreviewCharts = ({
  error,
  resetError,
  onRemove,
}: SentryErrorComponentProps & { onRemove?: () => void }) => {
  const derivedError = error instanceof Error ? error : new Error(String(error));
  return (
    <div className="w-full h-full flex flex-col gap-2 items-center justify-center p-4 border rounded-md bg-[var(--color-card-muted)]">
      <FiAlertTriangle className="w-6 h-6 text-danger" />
      <p className="text-center text-danger">An error occurred while loading the chart.</p>
      <div className="flex gap-2">
        <Button
          onClick={resetError}
          className="flex gap-2 text-nowrap items-center px-3 w-full justify-center"
          color="primary"
        >
          <FiRefreshCw className="w-4 h-4" />
          <span>Try Again</span>
        </Button>
        <Button
          onClick={async () => {
            const message = derivedError.message || "An unknown error occurred";
            try {
              await navigator.clipboard.writeText(message);
              toast.success("Error message copied to clipboard", {
                icon: <FaCopy />,
                style: {
                  background: "var(--color-neutral)",
                  color: "var(--color-foreground)",
                },
              });
            } catch {
              toast.error("Failed to copy error message");
            }
          }}
          className="flex gap-2 text-nowrap items-center px-3 w-full justify-center"
          color="neutral"
        >
          <FiCopy className="w-4 h-4" />
          <span>Copy Error Message</span>
        </Button>
      </div>
      {onRemove && (
        <Button onClick={onRemove} className="flex gap-2 text-nowrap items-center px-3 justify-center" color="danger">
          <FiTrash2 className="w-4 h-4" />
          <span>Remove Chart</span>
        </Button>
      )}
    </div>
  );
};

export const ErrorPreview = ({ error, resetError, componentStack }: SentryErrorComponentProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const derivedError = error instanceof Error ? error : new Error(String(error));
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-2xl border border-red-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-danger to-brand p-4 text-white">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-full">
              <FiAlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Something went wrong</h1>
              <p className="text-red-100 text-sm mt-1">An unexpected error occurred in the application</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Error Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="space-y-1">
              <div className="flex items-start w-full">
                <FaBug className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                <h3 className="font-semibold text-red-800 mb-1">Error Details</h3>
              </div>

              <p className="text-red-700 text-sm font-mono bg-red-100 p-2 rounded border">
                {derivedError.message || "An unknown error occurred"}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={resetError}
              className="flex gap-2 text-nowrap items-center px-3 w-full justify-center"
              color="primary"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span>Try Again</span>
            </Button>

            <Button
              onClick={() => setShowDetails(!showDetails)}
              className="flex gap-2 text-nowrap items-center px-3 w-full justify-center"
              color="neutral"
            >
              {showDetails ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
              <span>{showDetails ? "Hide" : "Show"} Technical Details</span>
            </Button>
          </div>

          {/* Technical Details */}
          {showDetails && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800 border-b border-gray-300 pb-2">Technical Information</h3>

              {/* Stack Trace */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Stack Trace:</h4>
                <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto font-mono max-h-40 overflow-y-auto">
                  {derivedError.stack || "No stack trace available"}
                </pre>
              </div>

              {/* Component Stack */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Component Stack:</h4>
                <pre className="bg-gray-900 text-blue-400 p-3 rounded text-xs overflow-x-auto font-mono max-h-32 overflow-y-auto">
                  {componentStack || "No component stack available"}
                </pre>
              </div>

              {/* Error Info */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Error Type:</h4>
                <p className="text-sm text-gray-600 bg-gray-100 p-2 rounded font-mono">
                  {derivedError.name || "Unknown"}
                </p>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-800 mb-2">What can you do?</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Try refreshing the page or clicking &quot;Try Again&quot;</li>
              <li>• Check your internet connection</li>
              <li>• If the problem persists, copy the error details and contact support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
